import { telegram } from './telegram';
import type { Well } from '../types';
import { BASINS, type Basin } from '../types';
import { requestPreciseLocation } from './highPrecisionLocation';

export interface RouteInfo {
  distance: string;
  duration: string;
  summary: string;
}

export interface Coordinates {
  lat: number;
  lng: number;
}

// Track if geolocation was denied to avoid repeated permission prompts/errors
let geolocationDenied = false;

export class NavigationService {
  // Use environment variable - must be configured in .env as VITE_GOOGLE_MAPS_API_KEY
  private static readonly GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

  /**
   * Opens Google Maps with driving directions to the well
   * Uses deep link to launch native Google Maps app
   */
  static openGoogleMaps(well: Well) {
    const { latitude, longitude } = well;
    
    // Universal deep link that works on iOS and Android
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving&dir_action=navigate`;
    
    // Open in external browser/app
    telegram.webApp.openLink(mapsUrl, {
      try_instant_view: false
    });
    
    // Provide haptic feedback
    
    // Log navigation
    this.logNavigation(well);
  }

  /**
   * Opens Google Maps with a waypoint (e.g., route starting point) before the destination
   * Useful for caliche road navigation where you need to get to the main road first
   */
  static openGoogleMapsWithWaypoint(
    waypoint: Coordinates,
    destination: Coordinates
  ) {
    // Google Maps URL with waypoint
    // Format: origin -> waypoint -> destination
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination.lat},${destination.lng}&waypoints=${waypoint.lat},${waypoint.lng}&travelmode=driving&dir_action=navigate`;
    
    // Open in external browser/app
    telegram.webApp.openLink(mapsUrl, {
      try_instant_view: false
    });
    
    // Provide haptic feedback
    telegram.hapticFeedback('success');
  }

  /**
   * Opens Google Maps with multiple waypoints for caliche road navigation
   * Routes via pavement first, then follows the landmark/caliche route
   * Google Maps supports max 9 waypoints - we auto-simplify longer routes
   */
  static openGoogleMapsWithWaypoints(
    waypoints: Coordinates[],
    destination: Coordinates
  ) {
    // Google Maps URL supports max 9 waypoints
    const MAX_WAYPOINTS = 9;
    
    // If too many waypoints, simplify by keeping most important turns
    let routeWaypoints = waypoints;
    if (waypoints.length > MAX_WAYPOINTS) {
      routeWaypoints = this.simplifyWaypoints(waypoints, MAX_WAYPOINTS);
      console.log(`[Navigation] Simplified ${waypoints.length} waypoints to ${routeWaypoints.length}`);
    }
    
    // Format waypoints for Google Maps URL (pipe-separated)
    const waypointStr = routeWaypoints
      .map(w => `${w.lat},${w.lng}`)
      .join('|');
    
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination.lat},${destination.lng}&waypoints=${waypointStr}&travelmode=driving&dir_action=navigate`;
    
    telegram.webApp.openLink(mapsUrl, {
      try_instant_view: false
    });
    
    telegram.hapticFeedback('success');
  }

  /**
   * Simplify waypoints by keeping the most important ones (turns, not straight stretches)
   * Uses distance-based selection to keep points that deviate most from straight line
   */
  private static simplifyWaypoints(waypoints: Coordinates[], maxPoints: number): Coordinates[] {
    if (waypoints.length <= maxPoints) return waypoints;
    
    // Always keep first and last waypoints
    const result: Coordinates[] = [waypoints[0]];
    const remaining = waypoints.slice(1, -1);
    const lastWaypoint = waypoints[waypoints.length - 1];
    
    // Calculate importance score for each middle waypoint based on angle deviation
    const scoredWaypoints = remaining.map((wp, i) => {
      const prev = i === 0 ? waypoints[0] : remaining[i - 1];
      const next = i === remaining.length - 1 ? lastWaypoint : remaining[i + 1];
      
      // Calculate angle at this point - sharper angles = more important
      const angle1 = Math.atan2(wp.lat - prev.lat, wp.lng - prev.lng);
      const angle2 = Math.atan2(next.lat - wp.lat, next.lng - wp.lng);
      const angleDiff = Math.abs(angle1 - angle2);
      
      return { waypoint: wp, score: angleDiff, originalIndex: i };
    });
    
    // Sort by importance (angle deviation) and keep top N
    const pointsNeeded = maxPoints - 2; // Account for first and last
    const topWaypoints = scoredWaypoints
      .sort((a, b) => b.score - a.score)
      .slice(0, pointsNeeded)
      .sort((a, b) => a.originalIndex - b.originalIndex) // Restore original order
      .map(item => item.waypoint);
    
    result.push(...topWaypoints, lastWaypoint);
    return result;
  }

  /**
   * Gets route preview information using Google Directions API
   */
  static async getRoutePreview(
    origin: Coordinates,
    destination: Coordinates
  ): Promise<RouteInfo | null> {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/directions/json?` +
        `origin=${origin.lat},${origin.lng}&` +
        `destination=${destination.lat},${destination.lng}&` +
        `mode=driving&` +
        `key=${this.GOOGLE_MAPS_API_KEY}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch route');
      }

      const data = await response.json();

      if (data.status !== 'OK' || !data.routes.length) {
        return null;
      }

      const route = data.routes[0];
      const leg = route.legs[0];

      return {
        distance: leg.distance.text,
        duration: leg.duration.text,
        summary: route.summary || 'Via highways',
      };
    } catch (error) {
      console.error('Route preview error:', error);
      return null;
    }
  }

  /**
   * Gets current user location
   * Use browser geolocation (cleaner UI than Telegram's popup)
   */
  static async getCurrentLocation(): Promise<Coordinates | null> {
    // Skip Telegram location API - it shows an ugly popup
    // Use browser geolocation directly for cleaner system UI
    
    // Skip if previously denied (avoid spam)
    if (geolocationDenied) {
      console.log('[NavigationService] Browser geolocation previously denied, skipping');
      return null;
    }

    console.log('[NavigationService] Requesting high-precision location...');
    const result = await requestPreciseLocation({
      desiredAccuracyMeters: 20,
      refinementTimeoutMs: 6000,
      allowLastKnownFallback: false,
      allowTelegramFallback: false,
    });

    if (!result.fix) {
      if (result.reason && result.reason.toLowerCase().includes('denied')) {
        geolocationDenied = true;
      }
      console.log('[NavigationService] High-precision location failed:', result.reason);
      return null;
    }

    geolocationDenied = false;
    return {
      lat: result.fix.lat,
      lng: result.fix.lng,
    };
  }

  /**
   * Calculates distance between two coordinates (Haversine formula)
   */
  static calculateDistance(coord1: Coordinates, coord2: Coordinates): number {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRad(coord2.lat - coord1.lat);
    const dLng = this.toRad(coord2.lng - coord1.lng);
    
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(coord1.lat)) *
        Math.cos(this.toRad(coord2.lat)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private static toRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Shows confirmation dialog before navigating
   */
  static async confirmNavigation(well: Well, routeInfo?: RouteInfo | null): Promise<boolean> {
    return new Promise((resolve) => {
      let message = `Navigate to ${well.wellName}?`;
      
      if (routeInfo) {
        message += `\n\n📍 Distance: ${routeInfo.distance}\n⏱️ Time: ${routeInfo.duration}`;
      }

      telegram.webApp.showConfirm(message, (confirmed: boolean) => {
        resolve(confirmed);
      });
    });
  }

  /**
   * Shows popup prompting user to download offline maps
   */
  static promptOfflineMapDownload() {
    // Use confirm dialogs instead of showPopup for better compatibility
    
    // First, ask which basin
    const message = 
      '🗺️ Download Offline Maps\n\n' +
      'Select a basin:\n\n' +
      '1️⃣ Permian Basin (Southeast NM)\n' +
      '2️⃣ San Juan Basin (Northwest NM)\n' +
      '3️⃣ Raton Basin (Northeast NM)\n\n' +
      'Reply with the number (1, 2, or 3):';
    
    telegram.showAlert(message);
    
    // Show buttons using Telegram's button system
    if (telegram.webApp.showPopup) {
      telegram.webApp.showPopup({
        title: '🗺️ Select Basin',
        message: 'Choose which basin to download:',
        buttons: [
          { id: 'permian', type: 'default', text: 'Permian' },
          { id: 'san-juan', type: 'default', text: 'San Juan' },
          { id: 'raton', type: 'default', text: 'Raton' },
          { id: 'cancel', type: 'cancel', text: 'Cancel' }
        ]
      }, (buttonId: string) => {
        if (buttonId && buttonId !== 'cancel') {
          this.downloadOfflineMap(buttonId as Basin);
        }
      });
    } else {
      // Fallback: use confirm dialogs
      setTimeout(() => {
        telegram.showConfirm('Download Permian Basin?', (yes: boolean) => {
          if (yes) {
            this.downloadOfflineMap('permian');
          } else {
            telegram.showConfirm('Download San Juan Basin?', (yes: boolean) => {
              if (yes) {
                this.downloadOfflineMap('san-juan');
              } else {
                telegram.showConfirm('Download Raton Basin?', (yes: boolean) => {
                  if (yes) {
                    this.downloadOfflineMap('raton');
                  }
                });
              }
            });
          }
        });
      }, 500);
    }
  }

  /**
   * Initiates offline map download for specified basin
   */
  static downloadOfflineMap(basin: Basin) {
    const basinInfo = BASINS[basin];
    const { center, bounds } = basinInfo;
    
    // Calculate appropriate zoom level based on bounds
    const latDiff = bounds.north - bounds.south;
    const lngDiff = bounds.east - bounds.west;
    const maxDiff = Math.max(latDiff, lngDiff);
    const zoom = Math.floor(14 - Math.log2(maxDiff));
    
    // Google Maps deep link for offline download
    const androidDeepLink = `geo:${center.lat},${center.lng}?z=${zoom}&q=${encodeURIComponent(basinInfo.name)}`;
    
    if (telegram.isAndroid()) {
      // Android: Open Google Maps directly
      window.open(androidDeepLink, '_blank');
      
      // Show instructions
      setTimeout(() => {
        telegram.showAlert(
          `📍 Google Maps is Opening\n\n` +
          `To download ${basinInfo.name} offline map:\n\n` +
          `1. When map opens, look at bottom tabs\n` +
          `2. Swipe the tabs LEFT (past Directions, Start, Save, Share, Add label)\n` +
          `3. Tap "More" (three dots)\n` +
          `4. Tap "Download offline map"\n` +
          `5. Adjust the map area to cover ${basinInfo.name}\n` +
          `6. Tap "Download"\n\n` +
          `💾 Usually 50-200 MB per basin\n` +
          `📶 Works without internet!`
        );
      }, 1500);
    } else {
      // iOS or desktop
      const webUrl = `https://www.google.com/maps/@${center.lat},${center.lng},${zoom}z`;
      window.open(webUrl, '_blank');
      
      telegram.showAlert(
        `🗺️ Download ${basinInfo.name} Offline Map\n\n` +
        `📱 ANDROID - Google Maps:\n` +
        `1. Swipe bottom tabs LEFT (past Directions, Start, Save, Share)\n` +
        `2. Tap "More" (three dots •••)\n` +
        `3. Tap "Download offline map"\n` +
        `4. Adjust map area to cover ${basinInfo.name}\n` +
        `5. Tap "Download"\n\n` +
        `🍎 iOS - Apple Maps:\n` +
        `1. Open Apple Maps\n` +
        `2. Search for "${basinInfo.name}"\n` +
        `3. Swipe up on location card\n` +
        `4. Tap "Download" button\n\n` +
        `💾 Size: 50-200 MB per basin\n` +
        `📶 Works completely offline!`
      );
    }
  }

  private static logNavigation(well: Well) {
    // Log to CloudStorage for analytics
    telegram.webApp.CloudStorage.getItem('navigation_history', (error: string | null, value?: string) => {
      if (error) return;
      
      const history = value ? JSON.parse(value) : [];
      history.unshift({
        wellId: well.id,
        api: well.api,
        timestamp: new Date().toISOString(),
      });
      
      // Keep last 50 navigations
      const trimmed = history.slice(0, 50);
      telegram.webApp.CloudStorage.setItem('navigation_history', JSON.stringify(trimmed));
    });
  }
}
